/**
 * useStartUserPreview — déclenche le mode "Voir comme cet utilisateur" pour le SuperAdmin.
 *
 * - Vérifie côté serveur (RPC SECURITY DEFINER) que l'appelant est SuperAdmin.
 * - Journalise l'ouverture de session dans `admin_user_preview_audit_logs`.
 * - Stocke le contexte preview côté frontend.
 * - Redirige vers le dashboard correspondant au rôle cible.
 *
 * Ne touche JAMAIS la session Supabase Auth du SuperAdmin.
 */
import { useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useSuperAdminPreview } from "@/contexts/SuperAdminPreviewContext";
import type { UserRole } from "@/contexts/AuthContext";

type RpcErrorLike = {
  message?: string | null;
  details?: string | null;
  hint?: string | null;
  code?: string | null;
};

type PreviewContextRpc = {
  role?: string | null;
  full_name?: string | null;
  email?: string | null;
  profile?: {
    full_name?: string | null;
    email?: string | null;
  } | null;
};

function formatRpcError(error: unknown): string {
  if (!error) return "Erreur inconnue";
  if (error instanceof Error) return error.message;

  const e = error as RpcErrorLike;
  const parts = [
    e.message,
    e.details ? `Détails : ${e.details}` : null,
    e.hint ? `Indice : ${e.hint}` : null,
    e.code ? `Code : ${e.code}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" — ") : JSON.stringify(error);
}

function normalizePreviewContext(ctx: unknown): PreviewContextRpc | null {
  if (Array.isArray(ctx)) return (ctx[0] as PreviewContextRpc | undefined) ?? null;
  return (ctx as PreviewContextRpc | null) ?? null;
}

function normalizeSessionId(session: unknown): string | null {
  if (typeof session === "string") return session;
  if (session && typeof session === "object") {
    const row = Array.isArray(session) ? session[0] : session;
    const candidate = row as { id?: unknown; session_id?: unknown; start_user_preview?: unknown };
    if (typeof candidate.id === "string") return candidate.id;
    if (typeof candidate.session_id === "string") return candidate.session_id;
    if (typeof candidate.start_user_preview === "string") return candidate.start_user_preview;
  }
  return null;
}

function dashboardForRole(role: UserRole | null): string {
  switch (role) {
    case "admin": return "/admin";
    case "admin_centrale": return "/centrale/dashboard";
    case "admin_magasin": return "/magasin/admin/dashboard";
    case "admin_fournisseur": return "/fournisseur/admin/dashboard";
    case "kam": return "/kam/dashboard";
    case "supplier_guest": return "/qr-dashboard";
    case "chef_rayon":
    case "chef_secteur_magasin":
    case "collaborateur_magasin":
    case "fournisseur":
      return "/dashboard";
    default:
      return "/dashboard";
  }
}

export { dashboardForRole };

export function useStartUserPreview() {
  const navigate = useNavigate();
  const { setPreview } = useSuperAdminPreview();

  return useCallback(async (params: {
    targetUserId: string;
    targetLabel: string;
    reason?: string;
  }) => {
    try {
      // 1. Récupère le rôle/profil cible (RPC admin-only).
      const { data: ctx, error: ctxErr } = await supabase.rpc("get_preview_user_context", {
        p_target_user_id: params.targetUserId,
      });
      if (ctxErr) {
        console.error("[useStartUserPreview] get_preview_user_context failed", { ctxErr, targetUserId: params.targetUserId });
        throw ctxErr;
      }
      const previewContext = normalizePreviewContext(ctx);
      const role = previewContext?.role as UserRole | null ?? null;

      // Garde-fou : rôle cible inexploitable → ne pas démarrer le preview.
      if (!role) {
        console.error("[useStartUserPreview] Aucun rôle exploitable", { targetUserId: params.targetUserId, ctx });
        toast.error("Impossible de visualiser cet utilisateur : aucun rôle exploitable.");
        return;
      }

      const targetRoute = dashboardForRole(role);
      console.info("[useStartUserPreview] starting preview", {
        pathname: window.location.pathname,
        realAuthUserId: (await supabase.auth.getUser()).data.user?.id ?? null,
        targetUserId: params.targetUserId,
        targetLabel: params.targetLabel,
        rawRole: previewContext?.role,
        resolvedRole: role,
        targetRoute,
      });

      // 2. Journalise l'ouverture.
      const { data: sessionId, error: startErr } = await supabase.rpc("start_user_preview", {
        p_target_user_id: params.targetUserId,
        p_reason: params.reason ?? null,
        p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      });
      if (startErr) {
        console.error("[useStartUserPreview] start_user_preview failed", { startErr, targetUserId: params.targetUserId, role });
        throw startErr;
      }

      const normalizedSessionId = normalizeSessionId(sessionId);
      if (!normalizedSessionId) {
        throw new Error(`Format inattendu retourné par start_user_preview : ${JSON.stringify(sessionId)}`);
      }

      // 3. Active le contexte preview.
      flushSync(() => {
        setPreview({
          sessionId: normalizedSessionId,
          targetUserId: params.targetUserId,
          targetRole: role,
          targetProfile: previewContext?.profile ?? null,
          targetLabel: params.targetLabel || previewContext?.full_name || previewContext?.profile?.full_name || previewContext?.email || previewContext?.profile?.email || params.targetUserId,
          startedAt: new Date().toISOString(),
        });
      });

      // 4. Navigue vers le dashboard du rôle cible.
      navigate(targetRoute, { replace: true });
      console.info("[useStartUserPreview] preview activated → navigated to", targetRoute);
      toast.success(`Mode visualisation activé : ${params.targetLabel}`);
    } catch (e: unknown) {
      const msg = formatRpcError(e);
      console.error("[useStartUserPreview] failed:", { error: e, message: msg, targetUserId: params.targetUserId });
      toast.error(`Impossible d'activer le mode visualisation : ${msg}`);
    }
  }, [navigate, setPreview]);
}

export function useEndUserPreview() {
  const navigate = useNavigate();
  const { preview, clearPreview } = useSuperAdminPreview();

  return useCallback(async (opts?: { redirectTo?: string }) => {
    if (preview?.sessionId) {
      try {
        await supabase.rpc("end_user_preview", { p_session_id: preview.sessionId });
      } catch (e) {
        console.warn("[useEndUserPreview] end RPC failed (non-blocking):", e);
      }
    }
    clearPreview();
    navigate(opts?.redirectTo ?? "/admin", { replace: true });
  }, [preview, clearPreview, navigate]);
}
